import { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { Bar, BarChart, Cell, XAxis, YAxis } from "recharts"

import type { DashboardUseCase } from "../../../types/use-case.types"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"

import { SITE_CHART_COLORS } from "../../../types/use-case.types"
import { formatUSD } from "../../../utils/format-helpers"
import { usdFormatter } from "../usd-tooltip-formatter"

type SiteChartsProps = {
  siteMap: Map<string, DashboardUseCase[]>
}

type SiteChartData = {
  site: string
  fullSite: string
  benefit: number
  roi: number
  color: string
}

export function SiteCharts({ siteMap }: SiteChartsProps) {
  const { t } = useTranslation()

  const benefitChartConfig = useMemo(
    () =>
      ({
        benefit: { label: t("useCases.dashboard.charts.annualBenefit") },
      }) satisfies ChartConfig,
    [t]
  )

  const roiChartConfig = useMemo(
    () =>
      ({
        roi: { label: t("useCases.dashboard.charts.roi") },
      }) satisfies ChartConfig,
    [t]
  )

  const data = useMemo<SiteChartData[]>(() => {
    const sites = [...siteMap.keys()].sort()
    return sites.map((site, i) => {
      const ucs = siteMap.get(site) || []
      let benefit = 0
      let roi = 0
      for (const uc of ucs) {
        benefit += uc.annualBenefit
        roi += uc.roi12m
      }
      return {
        site: site.length > 20 ? site.slice(0, 17) + "..." : site,
        fullSite: site,
        benefit,
        roi,
        color: SITE_CHART_COLORS[i % SITE_CHART_COLORS.length],
      }
    })
  }, [siteMap])

  if (data.length === 0) return null

  const chartHeight = Math.max(200, data.length * 32)

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card className="rounded-lg shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">
            {t("useCases.dashboard.siteComparison.benefitBySite")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer config={benefitChartConfig} style={{ height: chartHeight }}>
            <BarChart
              data={data}
              layout="vertical"
              margin={{ left: 10, right: 30, top: 5, bottom: 5 }}
            >
              <XAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatUSD(v)} type="number" />
              <YAxis dataKey="site" tick={{ fontSize: 11 }} type="category" width={130} />
              <ChartTooltip content={<ChartTooltipContent />} formatter={usdFormatter} />
              <Bar dataKey="benefit" radius={[0, 4, 4, 0]}>
                {data.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={entry.color}
                    stroke="hsl(var(--foreground))"
                    strokeWidth={1}
                  />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>

      <Card className="rounded-lg shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">
            {t("useCases.dashboard.siteComparison.roiBySite")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer config={roiChartConfig} style={{ height: chartHeight }}>
            <BarChart
              data={data}
              layout="vertical"
              margin={{ left: 10, right: 30, top: 5, bottom: 5 }}
            >
              <XAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatUSD(v)} type="number" />
              <YAxis dataKey="site" tick={{ fontSize: 11 }} type="category" width={130} />
              <ChartTooltip content={<ChartTooltipContent />} formatter={usdFormatter} />
              <Bar dataKey="roi" radius={[0, 4, 4, 0]}>
                {data.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={entry.color}
                    stroke="hsl(var(--foreground))"
                    strokeWidth={1}
                  />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  )
}
